import React from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  alpha,
  Stack,
  CircularProgress,
} from '@mui/material';
// import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import {
  BACKGROUND_GRADIENT_TRANSPARENT,
  BUTTON_BG_COLOR,
} from './AutoSpotlightDialog';
import DoneIcon from '@mui/icons-material/Done';
import { typographyClamps } from '../../utils/utils';
import { NewLeadQuickOption } from './CarDetailsDialog';

interface PreOnboardingDetailsProps {
  onContinue: () => void;
  onBack: () => void;
  newLeadOption: NewLeadQuickOption;
  title?: string;
  description?: string;
  featureTitle?: string;
  featureDescription?: string;
  features: string[];
  submitButtonText?: string;
  footerText?: string;
  imagePath?: string;
  isLoading?: boolean;
}

const PreOnboardingDetails: React.FC<PreOnboardingDetailsProps> = ({
  title,
  description,
  featureTitle,
  featureDescription,
  features,
  submitButtonText,
  footerText,
  imagePath,
  onBack,
  onContinue,
  isLoading,
}) => {
  return (
    <Box
      sx={{
        pb: 3,
        overflowY: 'auto',
        borderRadius: { xs: 2, sm: 3 },
        flex: 1,
        background: BACKGROUND_GRADIENT_TRANSPARENT,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 3,
          py: 1,
          mb: 2,
          textAlign: 'center',
          background:
            'linear-gradient(to right, #B1CAFF, #ffffff, #ffffff, #ffffff)',
          borderBottom: '1px solid',
          borderColor: (theme) => alpha(theme.palette.primary.light, 0.1),
        }}
      >
        <IconButton
          onClick={onBack}
          sx={{ mr: 0.5, p: 0.5, pl: 0 }}
          aria-label='Go back'
        >
          <KeyboardBackspaceIcon sx={{ color: 'primary.light' }} />
        </IconButton>
        <Typography fontWeight={400} color={'primary.light'}>
          Back to Vehicle
        </Typography>
      </Box>

      <Box sx={{ px: 2 }}>
        <Typography
          variant='h6'
          fontWeight={500}
          sx={{ mb: 1, textAlign: 'center' }}
        >
          {title || ''}
        </Typography>

        <Typography variant='body2' sx={{ mb: 2, textAlign: 'center' }}>
          {description || ''}
        </Typography>

        {imagePath && (
          <Box m={0} textAlign={'center'}>
            <Box
              component={'img'}
              src={imagePath}
              sx={{
                objectFit: 'contain',
                maxWidth: { xs: '50%', sm: '30%' },
                mx: 'auto',
                my: 1,
              }}
            />
          </Box>
        )}

        {featureTitle && (
          <Typography variant='body2' fontWeight={'600'} sx={{ mt: 1 }}>
            {featureTitle}
          </Typography>
        )}

        {featureDescription && (
          <Typography variant='body2' sx={{ mt: 1 }}>
            {featureDescription}
          </Typography>
        )}

        <Stack spacing={0.5} mt={1}>
          {features.map((feature, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                gap: 1,
              }}
            >
              <DoneIcon color='primary' fontSize='small' />
              <Typography
                color='text.primary'
                sx={{ fontSize: typographyClamps.fontSize12 }}
              >
                {feature}
              </Typography>
            </Box>
          ))}
        </Stack>

        <Divider sx={{ my: 2, borderColor: 'rgba(102, 126, 234, 0.2)' }} />

        <Button
          fullWidth
          variant='contained'
          onClick={onContinue}
          sx={{
            py: 1.5,
            color: '#ffffff',
            background: BUTTON_BG_COLOR,
            textTransform: 'none',
          }}
        >
          {isLoading ? (
            <CircularProgress size={24} color='inherit' />
          ) : (
            (submitButtonText ?? 'button.continue')
          )}
        </Button>

        {footerText && (
          <Typography fontSize={'11px'} sx={{ mt: 1, textAlign: 'center' }}>
            {footerText}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default PreOnboardingDetails;
